import { DatabaseRecord, User, WellKnownPose } from ".";
import { DocumentReference } from "@um/contracts/firestore";
import type { Choreo } from "./choreo";

export type ModelProvider = "openai" | "azure" | "gemini";

// TODO: use a union type to show possible states
export interface ImageReference {
  id: string;
  svg?: string; // deprecated
  large?: string;
  small?: string;
  hash?: string; // deprecated
  original?: string;
  prompt?: string;
  error?: string;
  /** Machine-readable error code (e.g. "content_blocked") set alongside `error`. */
  errorCode?: string;
  yOffset?: number; // deprecated
  xOffset?: number; // deprecated
  metadata?: ImageMetadata;
  /** Native generation resolution, recorded for new expression sheets. */
  resolution?: "1K" | "2K";
  /** Pose-sheet framing. Absent on older sheets means foreground occlusion. */
  poseFraming?: "foreground" | "full-character";
  /** Background analysis of the finished artwork; images remain usable while this is pending. */
  poseAnalysis?: PoseSheetAnalysis;
  /**
   * Size of this SHEET relative to the character's other sheets, default 1.
   *
   * One of three factors that multiply together (persona x sheet x panel) to
   * give a character's final on-stage scale. This one absorbs the fact that two
   * sheets of the same character can be composed at different zooms — the
   * artwork inside the frames is simply drawn bigger on one than the other.
   */
  relativeScale?: number;
  /**
   * "performance": an expression sheet drawn for one performance (a story or gram cast), its 16 poses designed for
   * it; by convention panel 1 is the idle and speaking pose, 5 a neutral front reference, 16 the finale. A
   * description only: readers may prefer such a sheet, but nothing may require it, and it is never on A. A story's
   * performer is drawn on C by convention; its sheet works the same on any other letter, marked or not.
   */
  purpose?: "performance";
  /**
   * The 16 pose instructions a sheet drawn from its own poses (any letter but A, whatever its purpose) was drawn
   * from. Pose analysis rewrites the panel descriptions to what was drawn; a redraw draws these again, so staged
   * tokens such as "C7" keep their meaning. Absent on A and on a sheet of the standard 16.
   */
  posePlan?: string[];
  /**
   * Reusable pose sequences over this sheet's own panels (docs/choreo-proposal.md), written with the sheet, on any
   * letter whatever its purpose. A redraw with new poses replaces them, so their tokens never go stale.
   */
  choreos?: Choreo[];

  // paneled image
  selectedPanel?: PanelInfo;
  panels?: ImagePanelConfig & {
    details: Record<string, ImagePanelDetails>; // this may have some issues with firestore update() due to https://github.com/firebase/firebase-js-sdk/pull/7887
  };
}

export interface PanelInfo extends ImagePanelDetails, ImagePanelConfig {
  name: string; // Panel name
}

export type ImagePanelConfig = {
  columns: number;
  rows: number;
  width: number;
  height: number;
  baselineSessionId?: string;
};

/**
 * The side of the screen a pose addresses, as the viewer sees it: "right" means
 * the pose best fits interacting with someone placed to screen right. The side
 * of a single raised, waving or pointing hand decides it; otherwise the side the
 * head and eyes turn to; otherwise the body's turn. "center" is a front-on pose
 * addressing the audience, even with a symmetric gesture, both arms up or a
 * prop overhead. Left and right poses mirror to face a cue; center never does.
 */
export type PoseOrientation = "left" | "center" | "right";

export interface PoseSheetAnalysis {
  version: 1;
  status: "pending" | "analyzing" | "ready" | "failed";
  requestId: string;
  imageId: string;
  baselineImageId?: string;
  baselinePanelId?: string;
  /** Queue time lets the editor offer a retry if background delivery stalls. */
  requestedAt?: number;
  analyzedAt?: number;
  error?: string;
  /**
   * Enabled poses whose two size estimates disagreed, or that peek over an edge: their pose scale was kept, so
   * they follow the sheet's zoom. Check them in the review lineup.
   */
  scaleReview?: string[];
  /** Enabled poses the two facing answers disagreed on: the current facing was kept (an unset one got the first answer). */
  orientationReview?: string[];
  /** Last automatic values let later analysis preserve fields the user has changed. */
  applied?: {
    relativeScale?: number;
    panels: Record<string, { description?: string; orientation?: PoseOrientation; relativeScale?: number }>;
  };
}

export interface ImagePanelDetails {
  row: number;
  column: number;
  disabled?: boolean;
  sprite: BoundingBox; // original combined component bbox
  frame: BoundingBox; // square box computed from bbox + perimeter lines
  description?: string;
  constraints?: { top: boolean; bottom: boolean; left: boolean; right: boolean };
  pose?: WellKnownPose;
  orientation?: PoseOrientation;
  /**
   * Size of this ONE POSE relative to the others on its sheet, default 1.
   *
   * The last of the three scale factors. Panels are cropped to their own
   * sprite, so a crouching pose and a standing pose end up the same height in
   * frame even though the character is not the same height in both; this is
   * where that is corrected.
   */
  relativeScale?: number;
}

export interface BoundingBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const imageSubjectTypes = [
  "real animal",
  "stuffed animal",
  "person",
  "clay",
  "paper craft",
  "art project",
  "drawing",
  "coloring page",
  "graphic",
  "unknown",
] as const;
export type ImageSubjectType = (typeof imageSubjectTypes)[number];

export interface ImageMetadata {
  isUnsafe: boolean;
  unsafeReason?: string;
  isPhoto: boolean;
  subjectType: ImageSubjectType;
  containsPerson: boolean;
  dressedUpAs?: string;
}

export interface Image extends ImageReference, DatabaseRecord {
  targetPath?: string; // the path to the document and property used for managing image variations
  intentId?: string; // maps to the prompt variation template that was used
  intentScore?: number; // the score of the intent that was used
  uploadedBy?: DocumentReference<User>;
  isGenerated: boolean;
}

/** A thumbnail for lists, or the largest available picture for artwork. */
export function imageReferenceUrl(image: ImageReference | undefined, size: "small" | "large" = "large"): string | undefined {
  return size === "small" ? image?.small || image?.large || image?.original : image?.original || image?.large || image?.small;
}

export function imageReferenceUrls(image: ImageReference | undefined): string[] {
  return [...new Set([image?.original, image?.large, image?.small].filter((url): url is string => !!url))];
}

/** Bundled static artwork becomes an ordinary absolute URL when saved to a shared program or account. */
export function absoluteImageReference(image: ImageReference, baseUrl: string): ImageReference {
  const resolve = (url: string) => url.startsWith("/") ? new URL(url, baseUrl).href : url;
  return { ...image,
    ...(image.original ? { original: resolve(image.original) } : {}),
    ...(image.large ? { large: resolve(image.large) } : {}),
    ...(image.small ? { small: resolve(image.small) } : {}),
  };
}

/** Copy/remap each rendition without losing metadata, pose analysis or panel geometry. */
export async function mapImageReferenceUrls(
  image: ImageReference,
  map: (url: string) => Promise<string>
): Promise<ImageReference> {
  const urls = new Map<string, string>();
  for (const url of imageReferenceUrls(image)) urls.set(url, await map(url));
  return {
    ...image,
    ...(image.original ? { original: urls.get(image.original)! } : {}),
    ...(image.large ? { large: urls.get(image.large)! } : {}),
    ...(image.small ? { small: urls.get(image.small)! } : {}),
  };
}
